import angular from "angular";

import { AccountingRuleSetAccount, AccountingRuleSetRow, AccountingTarget, AccountingTargetListItem, AccountingTargets, CalculationRowType, IncomeTypesLogic, FileLogic, Tables, FileDelimiter, ImportResult, DataRow, Translations, Language, PaidSickLeaveKind, TrainingKind, EnumerationsLogic, CalcRowsLogic } from "@salaxy/core";
import { EditDialogKnownActions, UiHelpers, SessionService } from "../../services";
import { ApiCrudObjectController } from "../bases";

/**
 * UI logic for viewing and adding new (modifying) accounting targets.
 * Accounting targets define where the accounting data is sent and how it is mapped to a Chart of Accounts (CoA)
 */
export class AccountingTargetCrudController extends ApiCrudObjectController<AccountingTarget, AccountingTargetListItem> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "AccountingTargets",
    "SessionService",
    "UiHelpers",
    "$location",
    "$routeParams",
    "$timeout"
  ];

  /** If the current item is based on template, it should be here once loaded from server. */
  public template: AccountingTarget;

  /** import data */
  public rawData: string | null;
  public fileExtension: string | null;

  public accountFilter = "all";

  public showDistinct = false;

  public optionTypes = [];

  private entryCodeGroups = [
    { value: "incomeType", text: "Tulolaji" },
    { value: "totalType", text: "Laskettu erä" },
  ];

  private totalTypes = [
    { value: "totalPensionWorker", text: "Työntekijöiden TyEL-maksut" },
    { value: "totalPensionEmployer", text: "Työnantajan TyEL-maksu" },
    { value: "totalPension", text: "TyEL-maksut" },
    { value: "totalUnemploymentWorker", text: "Työntekijöiden työttömyysvakuutusmaksut" },
    { value: "totalUnemploymentEmployer", text: "Työnantajan työttömyysvakuutusmaksu" },
    { value: "totalUnemployment", text: "Työttömyysvakuutusmaksut" },
    { value: "totalSocialSecurityEmployer", text: "Työnantajan sairausvakuutusmaksu" },
    { value: "totalSocialSecurityWorker", text: "Työntekijältä erikseen perittävä sairausvakuutusmaksu" },
    { value: "totalTax", text: "Ennakonpidätykset" },
    { value: "totalTaxAtSource", text: "Lähdeverot" },
    { value: "totalPalkkaus", text: "Palkkaus.fi-palkkio" },
    { value: "totalWorkerPayment", text: "Työntekijän nettopalkka ja kulukorvaukset" },
    { value: "totalPayment", text: "Maksettu asiakasvaratilille" },
    { value: "totalAccidentInsurance", text: "Työtapaturma- ja ammattitautivakuutusmaksut" },
    { value: "totalGroupLifeInsurance", text: "Ryhmähenkivakuutusmaksut" },
    { value: "totalAccruedHolidayPay", text: "Kertyneet lomapalkat" },
    { value: "totalAccruedHolidayBonus", text: "Kertyneet lomarahat" },
    { value: "totalAccruedHolidaySocialSecurity", text: "Kertyneet lomapalkkojen sairausvakuutusmaksut" },
    { value: "totalAccruedHolidayPension", text: "Kertyneet lomapalkkojen TyEL-maksut" },
    { value: "totalAccruedHolidayUnemployment", text: "Kertyneet lomapalkkojen työttömyysvakuutusmaksut (TVR)" },
    { value: "totalAccruedGroupLifeInsurance", text: "Kertyneet lomapalkkojen ryhmähenkivakuutusmaksut" },
    { value: "totalAccruedAccidentInsurance", text: "Kertyneet lomapalkkojen työtapaturma- ja ammattitautivakuutusmaksut" },
    { value: "totalPaidHolidaySalary", text: "Maksetut loma-ajan palkat" },
    { value: "totalPaidHolidayBonus", text: "Maksetut lomarahat" },
    { value: "totalPaidHolidayCompensation", text: "Maksetut lomakorvaukset" },
    { value: "totalPaidHolidaySocialSecurity", text: "Maksetut lomapalkkojen sairausvakuutusmaksut" },
    { value: "totalPaidHolidayPension", text: "Maksetut lomapalkkojen TyEL-maksut" },
    { value: "totalPaidHolidayUnemployment", text: "Maksetut lomapalkkojen työttömyysvakuutusmaksut (TVR)" },
  ];

  private incomeTypes = IncomeTypesLogic.getAll(Translations.getLanguage() as any || Language.Fi).map((x) => ({ value: "" + x.code, text: x.code + " - " + x.label, title: x.description }));

  constructor(
    private fullApi: AccountingTargets,
    private sessionService: SessionService,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private $timeout: angular.ITimeoutService,
  ) { // Dependency injection
    super(fullApi, uiHelpers, $location, $routeParams);
  }

  /**
   * Implement IController
   */
  public $onInit() {
    super.$onInit();
  }

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/accounting-targets",
      detailsUrl: this.detailsUrl || "/accounting-targets/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/AccountingTargets.html",
      oDataOptions: {},
    };
  }

  /**
   * Additional setup after the item has been loaded.
   * @param item Item that has been loaded
   */
  public setCurrentRef(item: AccountingTarget) {
    super.setCurrentRef(item);
    if (this.current?.ruleSet?.templateId) {
      if (this.current.ruleSet.templateId !== this.template?.ruleSet?.templateId) {
        this.fullApi.getSingle(this.current.ruleSet.templateId).then((result) => {
          this.template = result;
        });
      }
    }
  }

  /**
   * Shows the detail view for the item.
   * Supports viewing public items from other users as read-only.
   * @param item Item may be either Container or list item.
   */
  public showDetails(item: AccountingTargetListItem | AccountingTarget): angular.ILocationService {
    // TODO: Consider adding this support in super
    if (this.parentController) {
      return this.parentController.showDetails(item);
    }
    return this.$location.url(this.getDefaults().detailsUrl + this.getId(item) + "@" + item.owner);
  }


  /** Gets entry description */
  public getEntryDescription(row: AccountingRuleSetRow) {
    switch (row.entryCodeGroup) {
      case "incomeType":
        return this.entryCodeGroups.find((x) => x.value === row.entryCodeGroup).text + ": " + this.incomeTypes.find((x) => x.value === row.entryCode).text;
      case "totalType":
        return this.entryCodeGroups.find((x) => x.value === row.entryCodeGroup).text + ": " + this.totalTypes.find((x) => x.value === row.entryCode).text;
    }
    return "";
  }

  /** Selects entry */
  public selectEntry(row: AccountingRuleSetRow) {
    if (row.entryCodeGroup) {
      if (row.entryCodeGroup === "incomeType") {
        this.optionTypes = this.incomeTypes;
      } else {
        this.optionTypes = this.totalTypes;
      }
    }

    const logic = {
      entryCodeGroups: this.entryCodeGroups,
      optionTypes: this.optionTypes,
      currentItem: null as any,
      onEntryCodeGroupChange: (value: string) => {
        if (value === "incomeType") {
          logic.optionTypes = this.incomeTypes;
        } else {
          logic.optionTypes = this.totalTypes;
        }

        // If the code group (ie. the parent typeahead value) changes, clear the entry code.
        if (logic.currentItem) {
          logic.currentItem.entryCode = null;
        }
      }
    };

    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/settings/EntryCodes.html",
      row,
      logic
    ).then((result) => {
      if (result.action == EditDialogKnownActions.Ok && result.item.entryCode && result.item.entryCodeGroup) {
        row.entryCode = result.item.entryCode;
        row.entryCodeGroup = result.item.entryCodeGroup;
      }
    });
  }

  /**
   * Shows the detail view with a new item that is a copy of the given item.
   * Please note that this method supports copying public items from other users. Override is required because the default cannoty copy from other users
   * @param item Item may be either Container or list item.
   */
   public showCopyAsNew(item: AccountingTargetListItem | AccountingTarget): angular.ILocationService {
    if (this.parentController) {
      return this.parentController.showCopyAsNew(item);
    }
    return this.$location.url(this.getDefaults().detailsUrl + this.getId(item) + "@" + item.owner + "/copy-as-new");
  }

  /**
   * Creates a copy of the current set
   * Changes the settings so that the accounting target becomes a new item
   * @param copySource Item (container item) to copy as new.
   */
  public copyItem(copySource: AccountingTarget): AccountingTarget {
    const copy = super.copyItem(copySource);
    copy.info.target.avatar.id = null;
    copy.info.target.avatar.displayName = "Kopio " + copy.info.target.avatar.displayName;
    copy.ruleSet.templateId = copySource.id + "@" + copySource.owner;
    copy.isReadOnly = false;
    copy.ruleSet.targetCoA = angular.copy(copy.ruleSet.accounts);
    return copy;
  }

  /** Selects the account */
  public selectAccount(row: AccountingRuleSetRow, isCredit: boolean) {
    const accounts = angular.copy(this.current.ruleSet.accounts);
    for (const account of accounts) {
      if (isCredit) {
        (account as any).isSelected = account.id === row.creditId;
      } else {
        (account as any).isSelected = account.id === row.debitId;
      }
    }

    this.uiHelpers.showDialog(
      "salaxy-components/report/modals/AccountNumbers.html",
      null,
      {
        accounts,
      },
    ).then((account) => {
      if (account) {
        if (isCredit) {
          row.creditId = account.id;
        } else {
          row.debitId = account.id;
        }
      }
    });
  }

  /**
   * Sets selectedAccount
   * @param selectedAccount selectedAccount
   */
  public selectTargetAccount(selectedAccount: AccountingRuleSetAccount) {
    const accounts = angular.copy(this.current.ruleSet.targetCoA);
    for (const account of accounts) {
      (account as any).isSelected = account.account === selectedAccount.account;
    }

    this.uiHelpers.showDialog(
      "salaxy-components/report/modals/AccountNumbers.html",
      null,
      {
        accounts,
      },
    ).then((account) => {
      if (account) {
        selectedAccount.account = account.account;
        selectedAccount.text = account.text;
      }
    });
  }

  /** Return targetCoA */
  public get hasTargetCoA(): boolean {
    return !!this.current.ruleSet.targetCoA.length;
  }

  private groupBy<T>(source: T[], getKey: (item: T) => string, sortKeys = false): { [key: string]: T[] } {
    let result = source.reduce((previousValue, currentValue) => {
      const key = getKey(currentValue);
      previousValue[key] = previousValue[key] || [];
      (previousValue[key]).push(currentValue);
      return previousValue;
    }, {});
    if (sortKeys) {
      result = Object.keys(result).sort().reduce((previousValue, key) => {
        previousValue[key] = result[key];
        return previousValue;
      }, {});
    }
    return result;
  }

  /** Gets the ruleset rouws grouped by account and grouping */
  public get groupedRows(): { [key: string]: AccountingRuleSetRow[] } {
    return this.uiHelpers.cache(this, "groupedRows", () => {
      if (!this.current) {
        return null;
      }
      const getKey = (row: AccountingRuleSetRow): string => {
        if (!row.isIncluded) {
          return "xxxx";
        }
        const debit = (this.getAccount(row.debitId)?.account || "9999") + (row.debitGrouping || "yyyy");
        const credit = (this.getAccount(row.creditId)?.account || "9999") + (row.creditGrouping || "yyyy");
        return (debit + credit) || "zzzz";
      };
      return this.groupBy(this.current.ruleSet.rows, getKey, true);
    });
  }

  /**
   * Get rows based on current ruleSet, account and isDebet
   * @param account iterated account from current.ruleSet.accounts
   * @param isDebet indicates if debet or credit account
   */
  public getRulesetByAccount(account: AccountingRuleSetAccount, isDebet: boolean): AccountingRuleSetRow[] {
    return this.current.ruleSet.rows.filter((row) => isDebet ? row.debitId === account.id : row.creditId === account.id);
  }

  /** Returns account details */
  public getAccount(id: string) {
    return this.current.ruleSet.accounts.find((x) => x.id === id);
  }

  /** Adds new account */
  public addAccount(addToEnd = false) {
    const idx = addToEnd ? this.current.ruleSet.accounts.length : 0;
    const copyRow = addToEnd ? this.current.ruleSet.accounts[this.current.ruleSet.accounts.length - 1] : this.current.ruleSet.accounts[0];
    const newRow = {
      isReadOnly: false,
      id: this.newGuid(),
      account: copyRow.account,
      text: copyRow.text,
    };
    this.current.ruleSet.accounts.splice(idx, 0, newRow);
  }

  /**
   * Add new account to TargetCoA
   * @param addToEnd tells if adds to end of array or not
   */
  public addTargetCoaAccount(addToEnd = false) {
    const idx = addToEnd ? this.current.ruleSet.targetCoA.length : 0;
    const copyRow = addToEnd ? this.current.ruleSet.targetCoA[this.current.ruleSet.targetCoA.length - 1] : this.current.ruleSet.targetCoA[0];
    const newRow = {
      isReadOnly: false,
      id: this.newGuid(),
      account: copyRow.account,
      text: copyRow.text,
    };
    this.current.ruleSet.targetCoA.splice(idx, 0, newRow);
  }

  /** Removes the account */
  public removeAccount(row: AccountingRuleSetAccount, removeFromTargetCoa = false) {
    if (!row.isReadOnly) {
      if (removeFromTargetCoa) {
        const idx = this.current.ruleSet.targetCoA.findIndex((x) => x === row);
        this.current.ruleSet.targetCoA.splice(idx, 1);
      } else {
        // TODO: Consider adding a check if the account is in use in some of the derived rulesets. Perhaps in saving?
        if (this.current.ruleSet.rows.some((r) => r.creditId === row.id || r.debitId === row.id)) {
          this.uiHelpers.showAlert("Tiliä ei voi poistaa", "Tili on käytössä tiliöintimallissa.");
          return;
        }
        const idx = this.current.ruleSet.accounts.findIndex((x) => x.id === row.id);
        this.current.ruleSet.accounts.splice(idx, 1);

      }
    }
  }

  /**
   * Validated row input
   * @param row Row to be validated
   */
  public isValidAccount(row: AccountingRuleSetAccount): boolean {
    if (!this.current.ruleSet.targetCoA.length) {
      return true;
    }
    return !!this.current.ruleSet.targetCoA.find((x) => x.account.toString() === row.account.toString());
  }

  /** Adds a new row */
  public addRow(row: AccountingRuleSetRow, addToStart = false) {
    const idx = row ? this.current.ruleSet.rows.findIndex((x) => x.id === row.id) : this.current.ruleSet.rows.length;
    const copyRow = row || (addToStart ? this.current.ruleSet.rows[0] : this.current.ruleSet.rows[this.current.ruleSet.rows.length - 1]);
    const newRow = {
      isReadOnly: false,
      enabled: copyRow.enabled,
      id: this.newGuid(),
      entryCodeGroup: copyRow.entryCodeGroup,
      entryCode: copyRow.entryCode,
      isIncluded: copyRow.isIncluded,
      debitId: copyRow.debitId,
      debitGrouping: copyRow.debitGrouping,
      creditId: copyRow.creditId,
      creditGrouping: copyRow.creditGrouping,
      flags: angular.copy(copyRow.flags),
    };
    this.current.ruleSet.rows.splice(addToStart ? 0 : idx, 0, newRow);
  }

  /** Removes the row */
  public removeRow(row: AccountingRuleSetRow) {
    const idx = this.current.ruleSet.rows.findIndex((x) => x.id === row.id);
    if (!row.isReadOnly) {
      this.current.ruleSet.rows.splice(idx, 1);
    }
  }

  /** Selects flags */
  public selectFlags(row: AccountingRuleSetRow) {
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/settings/Flags.html",
      row,
      {
        getFlagText: (flag: string) => this.getFlagText(flag),
        addFlag: (current: AccountingRuleSetRow) => {
          const newFlag =  {
            prefix: "",
            value: "",
            kind: "",
            dimensionValue: "",
          };
          const logic =
            {
              _userDefinedRow:{
                item: null,
                row: {
                  data: {
                    get kind() {
                      return logic._userDefinedRow.item.kind;
                    },
                    set kind(value) {
                      logic._userDefinedRow.item.kind = value;
                    },
                  },
                  get rowType() {
                    if (logic._userDefinedRow.item.prefix == "type") {
                      return logic._userDefinedRow.item._value;
                    }
                    else
                    {
                      return logic._userDefinedRow.item.value;
                    }
                  },
                  set rowType(value) {
                    if (logic._userDefinedRow.item.prefix == "type") {
                      logic._userDefinedRow.item._value = value;
                    }
                    else
                    {
                      logic._userDefinedRow.item.value = value;
                    }
                  },
                  get type() {
                    if (logic._userDefinedRow.item.prefix == "type") {
                      return logic._userDefinedRow.item.value;
                    }
                    else
                    {
                      return logic._userDefinedRow.item._value;
                    }
                  },
                  set type(value) {
                    if (logic._userDefinedRow.item.prefix == "type") {
                      logic._userDefinedRow.item.value = value;
                    }
                    else
                    {
                      logic._userDefinedRow.item._value = value;
                    }
                  }
                }
              },
              asUserDefinedRow: (item ) => {
                logic._userDefinedRow.item = item;
                return logic._userDefinedRow.row;
              },
              pensionCalculation: EnumerationsLogic.getEnumMetadata("PensionCalculation").values.map( x => ({ text: x.label, value: x.name })).concat({ value: "entrepreneurLike", text: "Sovellettava eläketurva: yrittäjä" }),
              selfPayment: [
                { text: "SALAXY.ENUM.InvoiceType.net.label", value: "net"},
                { text: "SALAXY.ENUM.InvoiceType.tax.label", value: "tax"},
                { text: "SALAXY.ENUM.InvoiceType.pension.label", value: "pension"},
                { text: "SALAXY.ENUM.InvoiceType.unemployment.label", value: "unemployment"}
              ],
              dimension: (this.sessionService.session?.settings?.costAccounting?.dimensions ?? []).map(x => ({ text: x.label, value: x.id })),
              prefixOptions: [
                { text: "SALAXY.NG1.EmploymentRelation.employmentRelation", value: "employmentType" },
                { text: "SALAXY.NG1.WorkerWizardComponent.employment.pension.title", value: "pensionCalculation" },
                { text: "SALAXY.NG1.CalcRowTypeComponent.rowType", value: "rowType" },
                { text: "SALAXY.NG1.CalcRowTypeComponent.wageType", value: "type" },
                { text: "SALAXY.MODEL.CostAccountingSettings.title", value: "dimension" },
                { text: "SelfPayment", value: "selfPayment" },
                { text: "Behavior", value: "behavior" },
                { text: "Regex", value: "regex" },
              ]
             };
          this.uiHelpers.openEditDialog(
            "salaxy-components/modals/settings/FlagSelection.html",
             newFlag,
             logic,
            ).then((addFlagResult) => {
              if (addFlagResult.action == EditDialogKnownActions.Ok) {
                if (addFlagResult.item.prefix == "type") {
                  if (addFlagResult.item.kind) {
                    current.flags.push(addFlagResult.item.prefix + "." + addFlagResult.item.value + "/" + addFlagResult.item.kind);
                  }
                  else {
                    current.flags.push(addFlagResult.item.prefix + "." + addFlagResult.item.value);
                  }
                }
                else if (addFlagResult.item.prefix == "rowType") {
                  if (addFlagResult.item.kind) {
                    current.flags.push(addFlagResult.item.prefix + "." + addFlagResult.item.value + "." + addFlagResult.item.kind);
                  }
                  else {
                    current.flags.push(addFlagResult.item.prefix + "." + addFlagResult.item.value);
                  }
                }
                else if (addFlagResult.item.prefix == "dimension") {
                    current.flags.push(addFlagResult.item.prefix + "." + addFlagResult.item.value + "." + addFlagResult.item.dimensionValue);
                }
                else
                {
                  current.flags.push(addFlagResult.item.prefix + "." + addFlagResult.item.value);
                }
              }
            });
        },
      }
    ).then((result) => {
      if (result.action == EditDialogKnownActions.Ok) {
        row.flags = result.item.flags.filter(x => !!x);
      }
    });
  }

  /** Paste data from clipboard. */
  public pasteFromClipboard(): Promise<string> {
    this.rawData = null;
    this.fileExtension = null;
    return (navigator as any).clipboard.readText().then((text) => {
      this.rawData = text;
      this.import();
      // Ensure refresh in UI
      return this.$timeout(() => {
        return this.rawData;
      });
    });
  }

  /**
   * Reads the file
   * @param file Selected file
   */
  public readFile(file: File): Promise<string> {
    this.rawData = null;
    this.fileExtension = null;
    if (!file) {
      return Promise.resolve(null);
    }
    // TODO detect if excel file and read content using Excel reader.
    if (file.name) {
      this.fileExtension = FileLogic.getExtensionFromFileName(file.name);
    }

    const read = new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => reject;
      reader.readAsText(file);
    });

    return read.then((text) => {
      this.rawData = text;
      this.import();
      // Ensure refresh in UI
      return this.$timeout(() => {
        return text;
      });
    });

  }

  /**
   * Import data
   */
  public import(): void {
    let tableData: ImportResult<DataRow> = null;
    if (this.fileExtension && this.fileExtension === "xlsx") {
      this.uiHelpers.showAlert("Tiedoston muotoa ei tueta", "Excel-tiedostot eivät ole vielä tuettuja. Tallenna Excel-taulukko csv-muodossa tai kopioi taulukko leikepöydän kautta.");
      return;
    }
    const fileDelimeter = FileLogic.getFileDelimiter(this.rawData);
    if (fileDelimeter === FileDelimiter.Unknown) {
      this.uiHelpers.showAlert("Tiedoston muotoa ei tueta", "Virheellinen");
      return;
    }
    const firstRow = Tables.getFirstRow(this.rawData, fileDelimeter);
    if (!firstRow.length) {
      this.uiHelpers.showAlert("Tiedoston rivejä ei voida lukea", "Virheellinen");
      return;
    }

    const keys = Object.keys(firstRow);
    const headers = !/\d/.test(firstRow[keys[0]]) && !/\d/.test(firstRow[keys[1]]) ? null : ["aaaa", "bbbb"];
    tableData = Tables.import(this.rawData, FileLogic.getTableFormatByDelimeter(fileDelimeter), FileLogic.getCsvLocale(fileDelimeter), headers);


    const validation = this.validateTableData(tableData);
    if (validation.validationSuccess) {
      const mappingResult = tableData.data.map((item) => {
        return { account: item[validation.account].toString(), text: item[validation.text] } as AccountingRuleSetAccount;
      });

      if (this.current.ruleSet.targetCoA.length) {
        this.uiHelpers.showConfirm("Tilikartan päivitys", "Haluatko korvata aikaisemman tilikarttasi tuodulla", "Kyllä", "En").then((result) => {
          if (result) {
            this.current.ruleSet.targetCoA = mappingResult;
          }
        });
      } else {
        this.current.ruleSet.targetCoA = mappingResult;
      }

    } else {
      this.uiHelpers.showAlert("Tiedoston muotoa ei tueta", "Virheellinen");
    }
  }

  /**
   * Sort accounts
   */
  public sortRulesetAccounts(): void {
    this.current.ruleSet.accounts.sort((a, b) => {
      if (a.account > b.account) {
        return 1;
      }

      if (a.account < b.account) {
        return -1;
      }

      return 0;
    });
  }

  /**
   * Updates ruleSet account with provided targetCoA account
   * @param targetCoaAccount targetCoA account
   */
  public updateRuleSetAccount(targetCoaAccount: AccountingRuleSetAccount) {
    const item = this.current.ruleSet.accounts.find(x => x.account === targetCoaAccount.account);
    if (item) {
      item.text = targetCoaAccount.text;
    }
  }

  /**
   * Updates provided account text to match targetCoa account
   * @param rulesetAccount ruleSet account
   */
  public updateAccountToMatchTargetCoa(rulesetAccount: AccountingRuleSetAccount) {
    const item = this.current.ruleSet.targetCoA.find(x => x.account === rulesetAccount.account);
    if (item) {
      rulesetAccount.text = item.text;
    }
  }

  /**
   * Return account text to ui
   */
   public get accountFilterText() {
    switch (this.accountFilter) {
      case "all":
        return "SALAXY.NG1.AccountingTargetDetailsComponent.filters.all";
      case "includedAndEnabled":
        return "SALAXY.NG1.AccountingTargetDetailsComponent.filters.includedAndEnabled";
      case "error":
        return "SALAXY.NG1.AccountingTargetDetailsComponent.filters.error";
    }
  }

  /**
   * Filter accounts
   */
  public get filteredAccounts(): AccountingRuleSetAccount[] {
    switch(this.accountFilter) {
      case "all":
        return this.current.ruleSet.accounts;
      case "includedAndEnabled":
        return this.current.ruleSet.accounts.filter(account => this.current.ruleSet.rows.find(x => x.enabled && x.isIncluded && (x.debitId === account.id || x.creditId === account.id)));
      case "error":
        return this.current.ruleSet.accounts.filter(x => !this.current.ruleSet.targetCoA.map(item => { return item.account; }).includes(x.account));
    }
  }

  /**
   * Get accounting rules acount for distinct view
   * @param account account id
   * @returns rules count as number
   */
  public getRulesCount(account: string): number {
    const ids = this.current.ruleSet.accounts.filter(x => x.account === account).map(x => x.id);
    switch(this.accountFilter) {
      case "all":
      case "error":
        return this.current.ruleSet.rows.filter(x => ids.indexOf(x.debitId) >= 0 || ids.indexOf(x.creditId) >= 0).length;
      case "includedAndEnabled":
          return this.current.ruleSet.rows.filter(x => x.enabled && x.isIncluded && (ids.indexOf(x.debitId) >= 0  || ids.indexOf(x.creditId) >= 0)).length;
    }
  }

  /** Returns flag text */
  public getFlagText(flag: string): {found: boolean, text: string} {
    if (!flag) {
      return { found: false, text: null };
    }
    const legacyFlagDescription = this.legacyFlags.find((x) => x.value === flag);
    if (legacyFlagDescription) {
      return { found: true, text: legacyFlagDescription.text };
    }

    const flagParts = flag.split(".");
    let text = "";
    switch (flagParts[0]) {
      case "employmentType":
        {
          text = EnumerationsLogic.getEnumLabel("EmploymentRelationType", flagParts[1]);
        }
        break;
      case "pensionCalculation":
        {
          if (flagParts[1] == "entrepereurLike") {
            text = "Sovellettava eläketurva: yrittäjä";
          } else {
            text = EnumerationsLogic.getEnumLabel("PensionCalculation", flagParts[1]);
          }
        }
        break;
      case "rowType":
        {
          if (flagParts.length > 2) {
            text = this.getCalculationRowTypeLabel(flagParts[1] as CalculationRowType, flagParts[2]);
          }
          else {
            text = this.getCalculationRowTypeLabel(flagParts[1] as CalculationRowType, null);
          }
        }
        break;
      case "type":
        {
          text = flag.replace("type.","");
        }
        break;
      case "dimension":
        {
          text = flag.replace("dimension.","");
        }
        break;
      case "selfPayment":
        {
          text = EnumerationsLogic.getEnumLabel("InvoiceType", flagParts[1]);
          if (text) {
            text = "Self payment: " + text;
          }
        }
        break;
      case "behavior":
          {
            text = EnumerationsLogic.getEnumLabel("CalculationFlag", flagParts[1]);
          }
          break;
      case "regex":
        {
          text = flag.replace("regex.","");
        }
        break;
      default:
        break;
      }

    if (text) {
      return {found: true, text };
    }

    return {found: false, text: flag };
  }

  /**
   * Created distinct object array with account key
   */
  public get distinctAccounts(): AccountingRuleSetAccount[] {
    return [...new Map(this.filteredAccounts.map(item => [item["account"], item])).values()];
  }

  /**
   * Acount number of digits in account candidate
   */
  private countDigits(accountCandidate: string): number {
    const digitsArr = accountCandidate.match(/\d+/g);
    if (digitsArr) {
      return digitsArr.join("").length;
    }
    return 0;
  }

  /**
   * Validated generated Tables data
   * @returns is TableData valid
   */
  private validateTableData(tableData: ImportResult<DataRow>): { validationSuccess: boolean, account: string, text: string } {
    //Validate if data exists
    if (!tableData.data) {
      return { validationSuccess: false, account: null, text: null };
    }

    //Validate errors
    if (tableData.errors.length !== 0) {
      return { validationSuccess: false, account: null, text: null };
    }

    // Validate data array
    if (tableData.data && tableData.data.length < 1) {
      return { validationSuccess: false, account: null, text: null };
    }

    // Validate first data array entry key and value
    for (const [key, value] of Object.entries(tableData.data[0])) {
      if (key.length < 2 || value.length < 2) {
        return { validationSuccess: false, account: null, text: null };
      }
    }

    // Validate numeric field
    for (const item of tableData.data) {
      const keys = Object.keys(item);
      // Check if item[keys[0]] or item[keys[0]] containt digits
      // If no return validation false
      // otherwise return validationSuccess true and figure out account and text
      const firstKey = this.countDigits(item[keys[0]].toString());
      const secondKey = this.countDigits(item[keys[1]].toString());
      return !/\d/.test(item[keys[0]]) && !/\d/.test(item[keys[1]]) ? { validationSuccess: false, account: null, text: null } : { validationSuccess: true, account: firstKey > secondKey ? keys[0] : keys[1], text: firstKey > secondKey ? keys[1] : keys[0] };
    }
  }
  private newGuid(): string {
    let dt = new Date().getTime();
    const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      /* eslint-disable-next-line no-bitwise */
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      /* eslint-disable-next-line no-bitwise */
      return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
    });
    return uuid;
  }

  private legacyFlags = [
    { value: "customerSelfPayment", text: "Työnantaja hoitaa" },
    { value: "cfDeductionAtSalaxy", text: "Palkkaus pidättää" },

    { value: "incomeEarnerEntrepreneur", text: "Sovellettava eläketurva: yrittäjä" },

    { value: `${CalculationRowType.Expenses}`, text: "Palkkalaji: Kulukorvaus" },
    { value: `${CalculationRowType.PrepaidExpenses}`, text: "Palkkalaji: Kulukorvausennakko" },
    { value: `${CalculationRowType.Foreclosure}`, text: "Palkkalaji: Ulosotto"},
    { value: `${CalculationRowType.UnionPayment}`, text: "Palkkalaji: AY-jäsenmaksu" },
    { value: `${CalculationRowType.Advance}`, text: "Palkkalaji: Palkkaennakko" },
    { value: `${CalculationRowType.Service}`, text: "Palkkalaji: Palvelumaksu" },
    { value: `${CalculationRowType.HolidaySalary}`, text: "Palkkalaji: Loma-ajan palkka" },
    { value: `${CalculationRowType.PaidSickLeave}.${PaidSickLeaveKind.PaidSickLeaveSalary}`, text: "Palkkalaji: Palkallinen sairauspoissaolo, kertakorvaus"},
    { value: `${CalculationRowType.PaidSickLeave}.${PaidSickLeaveKind.PaidSickLeaveHourlySalary}`, text: "Palkkalaji: Palkallinen sairauspoissaolo, tuntipalkka" },
    { value: `${CalculationRowType.PaidSickLeave}.${PaidSickLeaveKind.PaidSickLeaveMonthlySalary}`, text: "Palkkalaji: Palkallinen sairauspoissaolo, kuukausipalkka" },
    { value: `${CalculationRowType.Training}.${TrainingKind.TrainingSalary}`, text: "Palkkalaji: Koulutusaika, kertakorvaus" },
    { value: `${CalculationRowType.Training}.${TrainingKind.TrainingHourlySalary}`, text: "Palkkalaji: Koulutusaika, tuntipalkka"},
    { value: `${CalculationRowType.Training}.${TrainingKind.TrainingMonthlySalary}`, text: "Palkkalaji: Koulutusaika, kuukausipalkka" },
    { value: `${CalculationRowType.DeductibleOfExerciseAndCultureBenefit}`, text: "Palkkalaji: Liikunta- ja kulttuuri-edun omavastuu" },
  ];

  private getCalculationRowTypeLabel(rowType: CalculationRowType, kind: string): string {
    let label = "";
    if (rowType === CalculationRowType.IrIncomeType) {
      label = IncomeTypesLogic.getAll(null).find( (x) => (kind && x.transactionCode === kind))?.label;
    }
    else if (kind) {
      const rowConfig = CalcRowsLogic.getRowConfig(rowType);
      label =  EnumerationsLogic.getEnumLabel(rowConfig.kind.name, kind, null, null);
    }
    return label || EnumerationsLogic.getEnumLabel("CalculationRowType", rowType, null, null);

  }
}
